/**
 * Create User Form
 * Single-page form for creating new users (Gutachter or Assistent)
 */

import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, AlertCircle, Building2 } from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import type { UserRole } from "@jourfx/types/users";

type CreateUserRole = Extract<UserRole, "gutachter" | "assistent">;
type AvailableLicense = {
  _id: string;
  kind: "base" | string;
  moduleId?: string | null;
  seats?: number | null;
  assignedCount: number;
};

// Convex ID type helper
type Id<T extends string> = string & { __tableName: T };

interface FormData {
  organizationId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: CreateUserRole;
  phone: string;
  sendInvite: boolean;
}

export function UserCreate() {
  const { t } = useTranslation('users');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const apiRef = api as any;

  // Form state
  const [formData, setFormData] = useState<FormData>({
    organizationId: "",
    email: "",
    firstName: "",
    lastName: "",
    role: "gutachter",
    phone: "",
    sendInvite: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedLicenseIds, setSelectedLicenseIds] = useState<string[]>([]);

  // Fetch organizations for dropdown
  const orgsResult = useQuery(api.admin.organizations.list, {});
  const organizations = orgsResult?.organizations ?? [];
  const orgsLoading = orgsResult === undefined;
  const licensesResult = useQuery(apiRef.admin.licenseAssignments.listAvailable, {});
  const licenseEntries = (licensesResult?.licenses ?? []) as AvailableLicense[];
  const licensesLoading = licensesResult === undefined;

  // Mutation for creating user
  const createUser = useMutation(api.admin.users.create);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!formData.organizationId) {
      setError(t('create.errors.selectOrganization'));
      return;
    }
    if (!formData.email) {
      setError(t('create.errors.enterEmail'));
      return;
    }
    if (!formData.email.includes("@")) {
      setError(tc('form.invalidEmail'));
      return;
    }
    if (!formData.firstName.trim()) {
      setError(t('create.errors.enterFirstName'));
      return;
    }
    if (!formData.lastName.trim()) {
      setError(t('create.errors.enterLastName'));
      return;
    }

    setIsSubmitting(true);

    try {
      await createUser({
        organizationId: formData.organizationId as Id<"organizations">,
        email: formData.email.trim().toLowerCase(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        role: formData.role,
        phone: formData.phone.trim() || undefined,
        sendInvite: formData.sendInvite,
        licenseIds: selectedLicenseIds as Id<"licenses">[],
      });

      // Success - navigate to users list
      navigate({ to: "/users" });
    } catch (err) {
      // Handle server errors
      const message = err instanceof Error ? err.message : tc('auth.errorOccurred');

      // Translate common error messages
      if (message.includes("already exists")) {
        setError(t('create.errors.userExists'));
      } else if (message.includes("Organization not found")) {
        setError(t('create.errors.organizationNotFound'));
      } else if (message.includes("license is not active")) {
        setError(t('create.errors.licenseNotActive'));
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleLicense = (licenseId: string, assigned: boolean) => {
    setSelectedLicenseIds((prev) => {
      if (assigned) {
        return prev.includes(licenseId) ? prev : [...prev, licenseId];
      }

      return prev.filter((id) => id !== licenseId);
    });
  };

  const getLicenseLabel = (license: AvailableLicense) => {
    if (license.kind === "base") {
      return t("create.baseLicense", "Base License");
    }

    const moduleLabel = t("create.moduleLicense", "Module License");
    return license.moduleId ? `${moduleLabel} (${license.moduleId})` : moduleLabel;
  };

  // Loading state for organizations
  if (orgsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Empty state - no organizations
  if (organizations.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/users" className="rounded-lg p-2 hover:bg-muted">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <h1 className="text-2xl font-bold text-foreground">{t('create.title')}</h1>
        </div>
        <div className="rounded-lg border bg-card p-8 text-center">
          <Building2 className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-4 text-lg font-medium text-foreground">
            {t('create.noOrganization.title')}
          </h3>
          <p className="mt-2 text-muted-foreground">
            {t('create.noOrganization.message')}
          </p>
          <Link
            to="/organizations/new"
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            {t('create.noOrganization.action')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/users" className="rounded-lg p-2 hover:bg-muted">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('create.title')}</h1>
          <p className="text-muted-foreground">{t('create.subtitle')}</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="rounded-lg border bg-card p-6">
        <div className="space-y-6">
          {/* Error Display */}
          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-error-muted p-4 text-error-muted-foreground">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Organization Select */}
          <div>
            <label
              htmlFor="organizationId"
              className="block text-sm font-medium text-foreground"
            >
              {t('create.organization')} *
            </label>
            <select
              id="organizationId"
              name="organizationId"
              value={formData.organizationId}
              onChange={handleChange}
              autoComplete="off"
              className="mt-1 block w-full rounded-lg border border-input px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              required
            >
              <option value="">{tc('form.pleaseSelect')}</option>
              {organizations.map((org: { _id: string; name: string }) => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-foreground"
            >
              {t('create.email')} *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              className="mt-1 block w-full rounded-lg border border-input px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t('create.emailPlaceholder')}
              required
            />
          </div>

          {/* Name Fields */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-foreground"
              >
                {t('create.firstName')} *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                autoComplete="given-name"
                className="mt-1 block w-full rounded-lg border border-input px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                required
              />
            </div>
            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-foreground"
              >
                {t('create.lastName')} *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                autoComplete="family-name"
                className="mt-1 block w-full rounded-lg border border-input px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                required
              />
            </div>
          </div>

          {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground">
                {t('create.role')} *
              </label>
              <div className="mt-2 flex gap-4">
              <label
                htmlFor="role-gutachter"
                aria-label={t('roles.gutachter')}
                className="flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-3 hover:bg-muted/50"
              >
                <input
                  type="radio"
                  id="role-gutachter"
                  name="role"
                  value="gutachter"
                  checked={formData.role === "gutachter"}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary"
                />
                <div>
                  <span className="font-medium">{t('roles.gutachter')}</span>
                  <p className="text-sm text-muted-foreground">{t('roleDescriptions.gutachter')}</p>
                </div>
              </label>
              <label
                htmlFor="role-assistent"
                aria-label={t('roles.assistent')}
                className="flex cursor-pointer items-center gap-2 rounded-lg border px-4 py-3 hover:bg-muted/50"
              >
                <input
                  type="radio"
                  id="role-assistent"
                  name="role"
                  value="assistent"
                  checked={formData.role === "assistent"}
                  onChange={handleChange}
                  className="h-4 w-4 text-primary"
                />
                <div>
                  <span className="font-medium">{t('roles.assistent')}</span>
                  <p className="text-sm text-muted-foreground">{t('roleDescriptions.assistent')}</p>
                </div>
              </label>
              </div>
            </div>

          {/* Phone (optional) */}
          <div>
            <label
              htmlFor="phone"
              className="block text-sm font-medium text-foreground"
            >
              {t('create.phone')}
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              autoComplete="tel"
              className="mt-1 block w-full rounded-lg border border-input px-3 py-2 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder={t('create.phonePlaceholder')}
            />
          </div>

          {/* Licensing */}
          <div className="space-y-4 rounded-lg border p-4">
            <div>
              <div className="text-sm font-medium text-foreground">
                {t("create.licensingTitle", "Licensing")}
              </div>
              <div className="text-sm text-muted-foreground">
                {t(
                  "create.licensingSubtitle",
                  "Assign seats before saving the user so access is ready immediately."
                )}
              </div>
            </div>

            {licensesLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading...</span>
              </div>
            )}

            {!licensesLoading && licenseEntries.length === 0 && (
              <div className="rounded-lg border border-warning/30 bg-warning/10 p-3 text-sm text-foreground">
                {t(
                  "create.noV2Licenses",
                  "No active v2 licenses are loaded for this installation. You can still create the user and assign seats later."
                )}
              </div>
            )}

            {!licensesLoading && licenseEntries.length > 0 && (
              <div className="space-y-2">
                {licenseEntries.map((license) => {
                  const inputId = `license-${license._id}`;
                  const isSelected = selectedLicenseIds.includes(license._id);
                  const isFull =
                    typeof license.seats === "number" &&
                    license.assignedCount >= license.seats &&
                    !isSelected;

                  return (
                    <div
                      key={license._id}
                      className="flex flex-col gap-2 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">
                          {getLicenseLabel(license)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t("create.seats", "Seats")}: {license.assignedCount}/{license.seats ?? "?"}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <input
                          id={inputId}
                          type="checkbox"
                          checked={isSelected}
                          disabled={isFull}
                          onChange={(event) => toggleLicense(license._id, event.target.checked)}
                        />
                        <label htmlFor={inputId}>{t("create.assigned", "Assign")}</label>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Send Invitation Checkbox */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="sendInvite"
              name="sendInvite"
              checked={formData.sendInvite}
              onChange={handleChange}
              className="h-4 w-4 rounded border-input text-primary focus:ring-primary"
            />
            <label htmlFor="sendInvite" className="text-sm text-foreground">
              {t('create.sendInvite')}
            </label>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end border-t pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSubmitting ? t('create.submitting') : t('create.submit')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
